import { get, patch, post } from './request';

export enum StandaloneChargeStatusEnum {
  pendingPayment = 'PENDING_PAYMENT',
  paid = 'PAID',
  cancelled = 'CANCELLED',
  expired = 'EXPIRED'
}

export enum StandaloneChargePaymentMethodEnum {
  pix = 'PIX',
  boleto = 'BOLETO'
}

export interface StandaloneChargeCreatedByUserDTO {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
}

export interface StandaloneChargeDTO {
  id: string;
  amount: number;
  description: string;
  payerName: string;
  payerCpf: string;
  payerEmail: string;
  payerPhone: string;
  payerAddressStreet?: string;
  payerAddressNumber?: string;
  payerAddressNeighborhood?: string;
  payerAddressComplement?: string;
  payerAddressZipCode?: string;
  payerAddressCity?: string;
  payerAddressState?: string;
  payerAddressCountry?: string;
  status: StandaloneChargeStatusEnum;
  paymentMethod: StandaloneChargePaymentMethodEnum;
  chargeLinkUrl: string;
  createdAt: string;
  expiresAt?: string;
  sentAt?: string;
  cancelledAt?: string;
  estate?: {
    id: string;
    name: string;
  };
  createdByUser?: StandaloneChargeCreatedByUserDTO;
}

/** Resposta de GET /standalone-charges/:id (campos extras do gateway e auditoria). */
export interface StandaloneChargeDetailDTO extends StandaloneChargeDTO {
  chargeLinkToken?: string;
  updatedAt?: string;
  paidAt?: string;
  gateway?: string;
  gatewayChargeId?: string;
  pagarmeOrderId?: string;
  pagarmeChargeId?: string;
  gatewayStatus?: string;
  lastGatewayPayload?: {
    lastEventId?: string;
    lastEventType?: string;
    lastUpdateAt?: string;
    paymentLink?: { id?: string; url?: string; status?: string; expiresAt?: string };
    webhook?: unknown;
  };
  estateSplitPercent?: number;
  platformSplitPercent?: number;
  pixQrCode?: string;
  pixQrCodeUrl?: string;
  pixCopyPaste?: string;
  pixExpiresAt?: string;
  boletoLine?: string;
  boletoBarcode?: string;
  boletoPdfUrl?: string;
  boletoDueAt?: string;
}

export interface ListStandaloneChargesResponseDTO {
  data: StandaloneChargeDTO[];
  count: number;
  page: number;
}

export interface ListStandaloneChargesFiltersDTO {
  page?: number;
  limit?: number;
  estateId?: string;
  payer?: string;
  document?: string;
  email?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreateStandaloneChargeDTO {
  estateId?: string;
  amount: number;
  payerName: string;
  payerCpf: string;
  payerEmail: string;
  payerPhone: string;
  payerAddressStreet?: string;
  payerAddressNumber?: string;
  payerAddressNeighborhood?: string;
  payerAddressComplement?: string;
  payerAddressZipCode?: string;
  payerAddressCity?: string;
  payerAddressState?: string;
  payerAddressCountry?: string;
  description: string;
  paymentMethod: StandaloneChargePaymentMethodEnum;
}

export interface StandaloneChargePublicDTO {
  id: string;
  amount: number;
  description: string;
  payerName: string;
  status: StandaloneChargeStatusEnum;
  paymentMethod: StandaloneChargePaymentMethodEnum;
  expiresAt?: string;
  paidAt?: string;
  pixQrCode?: string;
  pixQrCodeUrl?: string;
  pixCopyPaste?: string;
  pixExpiresAt?: string;
  boletoLine?: string;
  boletoBarcode?: string;
  boletoPdfUrl?: string;
  boletoDueAt?: string;
}

export interface StandaloneChargePublicStatusDTO {
  id: string;
  status: StandaloneChargeStatusEnum;
  paidAt?: string;
  expiresAt?: string;
  paymentMethod: StandaloneChargePaymentMethodEnum;
}

export type ActivationRecipientType = 'individual' | 'corporation';

export enum StandaloneChargeActivationStateEnum {
  notStarted = 'NOT_STARTED',
  active = 'ACTIVE',
  pagarmePending = 'PAGARME_PENDING',
  pagarmeActive = 'PAGARME_ACTIVE',
  pagarmeRejected = 'PAGARME_REJECTED'
}

export interface StandaloneChargeActivationDTO {
  id: string;
  status: StandaloneChargeActivationStateEnum;
  recipientType: ActivationRecipientType;
  completedAt?: string;
  updatedAt?: string;
  pagarmeRecipientId?: string;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  lastSyncError?: string;
  kycRequired?: boolean;
  kycLinkUrl?: string;
  kycQrPayload?: string;
}

export interface StandaloneChargeActivationStatusDTO {
  estateId: string;
  standaloneChargesEnabled: boolean;
  activation: StandaloneChargeActivationDTO | null;
}

export interface CreateStandaloneChargeActivationDTO {
  estateId?: string;
  code?: string;
  recipientType: ActivationRecipientType;
  registerInformation: Record<string, unknown>;
  defaultBankAccount: Record<string, unknown>;
  transferSettings?: Record<string, unknown>;
  automaticAnticipationSettings?: Record<string, unknown>;
  metadata?: unknown;
}

export const listStandaloneCharges = async (
  filters: ListStandaloneChargesFiltersDTO
): Promise<ListStandaloneChargesResponseDTO> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.append(key, String(value));
    }
  });

  return await get(`/standalone-charges?${params.toString()}`);
};

export const createStandaloneCharge = async (
  payload: CreateStandaloneChargeDTO
): Promise<StandaloneChargeDTO> => {
  return await post('/standalone-charges', payload);
};

export const getStandaloneCharge = async (id: string): Promise<StandaloneChargeDetailDTO> => {
  return await get(`/standalone-charges/${id}`);
};

export const getStandaloneChargeActivationStatus = async (
  estateId?: string
): Promise<StandaloneChargeActivationStatusDTO> => {
  const query = estateId ? `?estateId=${estateId}` : '';
  return await get(`/standalone-charges/activation-status${query}`);
};

export const activateStandaloneCharges = async (
  payload: CreateStandaloneChargeActivationDTO
): Promise<StandaloneChargeActivationStatusDTO> => {
  return await post('/standalone-charges/activation', payload);
};

export const resendStandaloneChargeEmail = async (id: string): Promise<{ success: boolean }> => {
  return await post(`/standalone-charges/${id}/resend-email`, {});
};

export const cancelStandaloneCharge = async (id: string): Promise<StandaloneChargeDTO> => {
  return await patch(`/standalone-charges/${id}/cancel`, {});
};

export const renewStandaloneCharge = async (id: string): Promise<StandaloneChargeDTO> => {
  return await post(`/standalone-charges/${id}/renew`, {});
};

export const getStandaloneChargePublic = async (
  token: string
): Promise<StandaloneChargePublicDTO> => {
  return await get(`/standalone-charges/public/${token}`);
};

export const getStandaloneChargePublicStatus = async (
  token: string
): Promise<StandaloneChargePublicStatusDTO> => {
  return await get(`/standalone-charges/public/${token}/status`);
};
